import { isTouchDevice } from "./device";
/** Thin fetch wrapper for the NutriCart API. Errors carry the server's stable `code`. */
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    public body: Record<string, any> = {},
  ) {
    super(code);
  }
}

export interface RequestOpts {
  /** Give up after this long (code "timeout") instead of leaving someone staring at a spinner. */
  timeoutMs?: number;
  /** Lets the screen cancel (code "cancelled"). */
  signal?: AbortSignal;
}

async function request<T>(method: string, url: string, body?: unknown, opts: RequestOpts = {}): Promise<T> {
  let res: Response;
  let text: string;
  // One controller for both the time limit and the screen's cancel button. Built by hand rather than with
  // AbortSignal.timeout / AbortSignal.any, which older iPhones (before iOS 16 / 17.4) don't have.
  const ctl = opts.timeoutMs || opts.signal ? new AbortController() : null;
  let timedOut = false;
  const timer = opts.timeoutMs
    ? window.setTimeout(() => {
        timedOut = true;
        ctl!.abort();
      }, opts.timeoutMs)
    : undefined;
  const cancel = () => ctl!.abort();
  if (opts.signal?.aborted) ctl?.abort();
  opts.signal?.addEventListener("abort", cancel);
  try {
    res = await fetch(url, {
      signal: ctl?.signal,
      method,
      credentials: "same-origin",
      headers: {
        "X-Nutricart": "1",
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    // Reading the answer is still part of the wait: the time limit and cancel cover it too.
    text = await res.text();
  } catch {
    if (opts.signal?.aborted) throw new ApiError(0, "cancelled");
    if (timedOut) throw new ApiError(0, "timeout");
    throw new ApiError(0, "offline");
  } finally {
    window.clearTimeout(timer);
    opts.signal?.removeEventListener("abort", cancel);
  }
  let json: any = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }
  if (!res.ok) {
    if (res.status === 401 && json?.error === "not_signed_in") window.dispatchEvent(new Event("nc:signed-out"));
    if (res.status === 402 && json?.error === "plus_required") window.dispatchEvent(new Event("nc:plus-required"));
    throw new ApiError(res.status, json?.error ?? "server_error", json ?? {});
  }
  return json as T;
}

export const api = {
  get: <T>(url: string) => request<T>("GET", url),
  post: <T>(url: string, body: unknown = {}, opts?: RequestOpts) => request<T>("POST", url, body, opts),
  put: <T>(url: string, body: unknown) => request<T>("PUT", url, body),
  patch: <T>(url: string, body: unknown) => request<T>("PATCH", url, body),
  del: <T>(url: string) => request<T>("DELETE", url),
};

/** Build a query string, skipping empty values. */
export function qs(params: Record<string, string | number | undefined | null | false>) {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== false && v !== "") s.set(k, String(v));
  const str = s.toString();
  return str ? `?${str}` : "";
}

/**
 * Download a file from an API GET endpoint (exports). Uses a blob so the session cookie is sent
 * and errors can be shown instead of navigating to a JSON error page.
 */
export async function download(url: string, fallbackName: string) {
  let res: Response;
  try {
    res = await fetch(url, { credentials: "same-origin" });
  } catch {
    // No connection: said as such, so the person knows to try again with signal.
    throw new ApiError(0, "offline");
  }
  if (!res.ok) {
    const code = await res
      .json()
      .then((b: { error?: string }) => b.error)
      .catch(() => undefined);
    throw new ApiError(res.status, code && code !== "server_error" ? code : "download_failed");
  }
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") ?? "";
  const name = /filename="([^"]+)"/.exec(cd)?.[1] ?? fallbackName;
  const file = new File([blob], name, { type: blob.type || "application/octet-stream" });
  // Phones (above all iPhones, and the app opened from the home screen or inside WeChat) often ignore a
  // download link made in the page. On a phone the file is handed to a sheet (see FileReady) that offers the
  // phone's own "save or send" menu, plus a plain link to the same file that the browser downloads itself.
  if (isTouchDevice()) {
    window.dispatchEvent(new CustomEvent<FileOffer>("nc:file-ready", { detail: { file, url } }));
    return;
  }
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
}

/** A file ready to be saved on a phone: the file itself, and the address it came from (for a plain download). */
export interface FileOffer {
  file: File;
  url: string;
}

export const photoUrl = (id: string) => `/api/photos/${id}`;
